import { useState, useEffect, useMemo } from 'react';
import { XIcon } from '@heroicons/react/outline';
import toast from 'react-hot-toast';
import {
  BadgeCheckIcon,
  TrashIcon,
  UsersIcon,
  PencilAltIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  BanIcon,
} from '@heroicons/react/outline';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';

import { ROLES, FeatureFlag } from '../constants';
import { UsersListItem } from '../types';
import {
  useDeleteUserResultMutation,
  useDeleteUserForOwnerMutation,
  usePushUserThroughReviewMutation,
  useSwitchUserMutation,
  useToggleFeatureForUserMutation,
  useUpdatePasswordForUserMutation,
  useReassignThinkerMentorMutation,
  useGetMentorsQuery,
  useGetTracksQuery,
  useOwnerUpdateUserMutation,
  useToggleExtendedTimeLimitMutation,
} from '../services/apiSlice';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';
import Input from '../components/Input';
import SwitchButton from '../components/Switch';
import { useAppSelector } from '../stores/AppStore';
import {
  selectHasOneOfTheRoles,
  selectUserId,
} from '../features/user/userSlice';
import Select, { GroupBase, OptionsOrGroups } from 'react-select';
import {
  TrackSelectOption,
  buildTrackSelectOptions,
  findTrackOption,
  formatTrackOptionLabel,
} from '../utils/trackOptions';
import {
  MentorSelectOption,
  buildMentorSelectOptions,
  findMentorOption,
  formatMentorOptionLabel,
} from '../utils/mentorOptions';
interface Properties {
  user: UsersListItem;
  setChangedPasswordAt: (date: string) => Promise<void>;
  setToggledFeature: (featureFlags?: string[]) => Promise<void>;
  setMentorInfo: (mentorName: string) => Promise<void>;
  setExtendedTimeLimit: (extendedTimeLimit?: boolean) => Promise<void>;
  setUpdatedUser: (user: Partial<UsersListItem>) => Promise<void>;
  showUserDetailsModal: (show: boolean) => void;
}

export default function UserDetailsModal({
  user,
  setChangedPasswordAt,
  setToggledFeature,
  setMentorInfo,
  setUpdatedUser,
  setExtendedTimeLimit,
  showUserDetailsModal,
}: Properties) {
  const navigate = useNavigate();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [password, setPassword] = useState<string>();
  const [isChangingMentor, setIsChangingMentor] = useState(false);
  const [selectedMentorId, setSelectedMentorId] = useState<string>();
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState(user?.firstName ?? '');
  const [editedLastName, setEditedLastName] = useState(user?.lastName ?? '');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState(user?.email ?? '');
  const [isChangingTrack, setIsChangingTrack] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(
    user?.followsTracks?.[0]?._id,
  );
  const isOwner = useAppSelector((state) =>
    selectHasOneOfTheRoles(state, [ROLES.owner]),
  );
  const isOwnerOrSysadmin = useAppSelector((state) =>
    selectHasOneOfTheRoles(state, [ROLES.owner, ROLES.sysadmin]),
  );
  const currentUserId = useAppSelector(selectUserId);

  const compactSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      fontSize: '14px',
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '32px',
      padding: '0 6px',
    }),
    input: (base: any) => ({
      ...base,
      margin: '0px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '32px',
    }),
    groupHeading: (base: any) => ({
      ...base,
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      color: 'rgb(71, 85, 105)',
      backgroundColor: 'rgb(241, 245, 249)',
      padding: '4px 8px',
      marginBottom: '2px',
    }),
  };
  const mentorCompactSelectStyles = {
    ...compactSelectStyles,
    control: (base: any) => ({
      ...base,
      minHeight: '40px',
      height: 'auto',
      fontSize: '14px',
    }),
    valueContainer: (base: any) => ({
      ...base,
      minHeight: '40px',
      height: 'auto',
      padding: '4px 6px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      minHeight: '40px',
      height: 'auto',
    }),
    option: (base: any) => ({
      ...base,
      whiteSpace: 'normal',
      padding: '4px 8px',
    }),
  };
  const [switchUser, { isLoading: isSwitchingUser }] = useSwitchUserMutation();
  const [pushUserThroughReview, { isLoading: isPushingThrough }] =
    usePushUserThroughReviewMutation();
  const [deleteUserResult, { isLoading: isDeletingResult }] =
    useDeleteUserResultMutation();
  const [deleteUserForOwner, { isLoading: isDeletingUserForOwner }] =
    useDeleteUserForOwnerMutation();
  const [updatePassword, { isLoading }] = useUpdatePasswordForUserMutation();
  const [toggleFeature, { isLoading: isTogglingFeature }] =
    useToggleFeatureForUserMutation();
  const [reassignMentor, { isLoading: isReassigningMentor }] =
    useReassignThinkerMentorMutation();
  const { data: mentors, isLoading: isLoadingMentors } = useGetMentorsQuery();
  const [toggleExtendedTimeLimit, { isLoading: isTogglingExtendedTime }] =
    useToggleExtendedTimeLimitMutation();
  const [ownerUpdateUser, { isLoading: isUpdatingUser }] =
    useOwnerUpdateUserMutation();
  const { data: tracks, isLoading: isLoadingTracks } = useGetTracksQuery(
    undefined,
    { skip: !isOwnerOrSysadmin || !user?.roles.includes(ROLES.thinker) },
  );
  const trackSelectOptions: OptionsOrGroups<
    TrackSelectOption,
    GroupBase<TrackSelectOption>
  > = useMemo(
    () => (tracks ? buildTrackSelectOptions(tracks, isOwnerOrSysadmin) : []),
    [tracks, isOwnerOrSysadmin],
  );
  const mentorSelectOptions: MentorSelectOption[] = useMemo(
    () => buildMentorSelectOptions(mentors ?? [], selectedMentorId),
    [mentors, selectedMentorId],
  );
  useEffect(() => {
    if (user?.mentor && mentors) {
      const currentMentor = mentors.find(
        (m) =>
          m._id === user.mentor ||
          `${m.firstName} ${m.lastName}` === user.mentor,
      );
      if (currentMentor) {
        setSelectedMentorId(currentMentor._id);
      }
    }
  }, [user?.mentor, mentors]);

  useEffect(() => {
    if (!user) {
      return;
    }
    setEditedFirstName(user.firstName);
    setEditedLastName(user.lastName);
    setEditedEmail(user.email);
    setSelectedTrackId(user.followsTracks?.[0]?._id);
  }, [user]);

  if (!user) {
    return <></>;
  }

  const fullName = `${user.firstName} ${user.lastName}`;
  const isDeleteConfirmationMatching = deleteConfirmation === user.email;

  const handleRemoveResult = async (student?: UsersListItem) => {
    if (!student || !student.lastSubmittedAvailableQuiz) {
      return;
    }
    const commonText = `${
      user.roles.includes(ROLES.student) ? 'current' : 'last'
    } (${student.lastSubmittedAvailableQuiz}) result of ${fullName}`;
    const confirmed = window.confirm(
      `Are you sure you want to remove the ${commonText}? This action cannot be undone.`,
    );

    if (confirmed) {
      try {
        await deleteUserResult({
          userId: student._id,
          quizId: student.lastSubmittedAvailableQuiz,
        }).unwrap();
      } catch {
        toast.error(
          `Some unexpected error happened while trying to delete the ${commonText}. Please try again later!`,
        );
        return;
      }
      toast.success(`The ${commonText} was deleted successfully.`);
      showUserDetailsModal(false);
    }
  };

  const handleMentorReassignment = async () => {
    if (!selectedMentorId) {
      toast.error('Please select a mentor');
      return;
    }

    const currentMentor = mentors?.find(
      (m) =>
        m._id === user.mentor || `${m.firstName} ${m.lastName}` === user.mentor,
    );
    if (currentMentor && selectedMentorId === currentMentor._id) {
      toast.error('Please select a different mentor');
      return;
    }

    const selectedMentor = mentors?.find((m) => m._id === selectedMentorId);
    if (
      window.confirm(
        `Are you sure you'd like to reassign ${fullName} to mentor ${selectedMentor?.firstName} ${selectedMentor?.lastName}?`,
      )
    ) {
      try {
        await reassignMentor({
          userId: user._id,
          mentorId: selectedMentorId,
        }).unwrap();

        if (selectedMentor) {
          await setMentorInfo(
            `${selectedMentor.firstName} ${selectedMentor.lastName}`,
          );
        }

        toast.success('Mentor successfully reassigned!');
        setIsChangingMentor(false);
        setSelectedMentorId(undefined);
      } catch (error: any) {
        Sentry.captureException(error, {
          tags: {
            request: 'ownerReassignMentor',
          },
        });
        toast.error(
          error?.data?.message ||
            'Some unexpected error happened while trying to reassign the mentor. Please try again later!',
        );
      }
    }
  };

  const handlePasswordChange = async () => {
    if (!password || password.trim().length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (
      window.confirm(
        "Are you sure you'd like to change the password of this user? Please make sure you notify the user about the new password!",
      )
    ) {
      try {
        const updatedUser = await updatePassword({
          userId: user._id,
          password,
        }).unwrap();
        if (updatedUser?.changedPasswordAt) {
          await setChangedPasswordAt(updatedUser.changedPasswordAt);
        }
        toast.success('Password successfully changed!');
        setIsChangingPassword(false);
        setPassword(undefined);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            request: 'ownerUpdatePasswordForUser',
          },
        });
        toast.error(
          'Some unexpected error happened while trying to update the password. Please try again later!',
        );
        return;
      }
    }
  };

  const handleNameChange = async () => {
    const firstName = editedFirstName.trim();
    const lastName = editedLastName.trim();
    if (!firstName || !lastName) {
      toast.error('First name and last name are required.');
      return;
    }
    if (firstName === user.firstName && lastName === user.lastName) {
      toast.error('Please change the name before saving.');
      return;
    }
    const warnings = [
      'This will change how the user’s name is displayed across the app.',
    ];
    if (!window.confirm(warnings.join('\n\n'))) {
      return;
    }

    try {
      await ownerUpdateUser({
        userId: user._id,
        firstName,
        lastName,
      }).unwrap();
      await setUpdatedUser({ firstName, lastName });
      toast.success('Name successfully updated!');
      setIsEditingName(false);
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { request: 'ownerUpdateName' },
      });
      toast.error(
        error?.data?.message ||
          'Failed to update user name. Please try again later.',
      );
    }
  };

  const handleEmailChange = async () => {
    const email = editedEmail.trim();
    if (!email) {
      toast.error('Email is required.');
      return;
    }
    if (email.toLowerCase() === user.email.toLowerCase()) {
      toast.error('Please change the email before saving.');
      return;
    }
    const warnings = [
      `This will change the user’s login email from ${user.email} to ${email}.`,
      'The old email will no longer work for login.',
      user.roles.includes(ROLES.thinker) || user.roles.includes(ROLES.mentor)
        ? 'A welcome/password setup email will be sent to the new email address.'
        : 'No welcome email will be sent for this role.',
      ...(currentUserId === user._id
        ? [
            'You are changing your own owner email address. If the new email is incorrect or inaccessible, you may lock yourself out after logout or session expiry.',
          ]
        : []),
    ];
    if (!window.confirm(warnings.join('\n\n'))) {
      return;
    }

    try {
      await ownerUpdateUser({
        userId: user._id,
        email,
      }).unwrap();
      await setUpdatedUser({ email });
      toast.success('Email successfully updated!');
      setIsEditingEmail(false);
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { request: 'ownerUpdateEmail' },
      });
      toast.error(
        error?.data?.message ||
          'Failed to update user email. Please try again later.',
      );
    }
  };

  const handleTrackChange = async () => {
    if (!selectedTrackId) {
      toast.error('Please select a track');
      return;
    }
    const selectedTrack = tracks?.find(
      (track) => track._id === selectedTrackId,
    );
    if (!selectedTrack) {
      toast.error('Selected track could not be found');
      return;
    }
    if (selectedTrackId === user.followsTracks?.[0]?._id) {
      toast.error('Please select a different track');
      return;
    }
    const confirmed = window.confirm(
      [
        'This will change the Thinker’s current Track going forward.',
        'Historical results will remain as-is and may still reference the previous Track.',
        'Current/future quiz links may change to the new Track.',
      ].join('\n\n'),
    );
    if (!confirmed) {
      return;
    }

    try {
      await ownerUpdateUser({
        userId: user._id,
        trackId: selectedTrackId,
      }).unwrap();
      await setUpdatedUser({
        followsTracks: [
          {
            _id: selectedTrack._id,
            track: selectedTrack.track,
            serialNum: selectedTrack.serialNum,
          },
          ...(user.followsTracks?.slice(1) ?? []),
        ],
      });
      toast.success('Thinker track successfully updated!');
      setIsChangingTrack(false);
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { request: 'ownerUpdateThinkerTrack' },
      });
      toast.error(
        error?.data?.message ||
          'Failed to update the Thinker track. Please try again later.',
      );
    }
  };

  const handlePushThroughReview = async () => {
    if (!user) {
      return;
    }
    try {
      const sure = window.confirm(
        "Are you sure you'd like to push this user through the current solution review?",
      );
      if (!sure) {
        return;
      }
      await pushUserThroughReview(user._id).unwrap();
    } catch (error: any) {
      let errorText = '';
      if (error?.status === 400 || error?.status === 403) {
        errorText += error?.data?.message;
      }
      errorText
        ? toast.error(errorText)
        : toast.error(
            `Some unexpected error happened while trying to push ${user?._id} through the current solution review. Please try again later!`,
          );
      return;
    }
    showUserDetailsModal(false);
    toast.success(
      `Successfully pushed user ${user?._id} through the current solution review.`,
    );
  };

  const handleToggleFeature = async (feature: string, enabled: boolean) => {
    if (!user) {
      return;
    }
    try {
      const updatedUser = await toggleFeature({
        userId: user._id,
        feature,
        enabled,
      }).unwrap();
      await setToggledFeature(updatedUser.featureFlags);
      toast.success(
        `Feature ${feature.split('-').join(' ')} was successfully ${
          enabled ? 'enabled' : 'disabled'
        } for user ${fullName}.`,
      );
    } catch {
      toast.error(
        `Some unexpected error happened while trying to toggle the feature ${feature
          .split('-')
          .join(' ')} for user ${fullName}. Please try again later!`,
      );
      return;
    }
  };

  const handleToggleExtendedTimeLimit = async (enabled: boolean) => {
    if (!user) {
      return;
    }
    try {
      await toggleExtendedTimeLimit({
        userId: user._id,
        extendedTimeLimit: enabled,
      }).unwrap();

      await setExtendedTimeLimit(enabled);
      toast.success(
        `Extended time limit (2x) was successfully ${
          enabled ? 'enabled' : 'disabled'
        } for user ${fullName}.`,
      );
    } catch {
      toast.error(
        `Failed to toggle extended time limit for user ${fullName}. Please try again later.`,
      );
      return;
    }
  };

  const handleDeleteUser = async () => {
    if (deleteConfirmation !== user.email) {
      toast.error('Type the user email exactly to confirm deletion.');
      return;
    }

    const warnings = [
      user.roles.includes(ROLES.mentor)
        ? 'This mentor may have assigned thinkers. They will remain assigned to the deleted mentor until reassigned.'
        : undefined,
      user.roles.includes(ROLES.teacher)
        ? 'This teacher may have assigned classes. Those class references will remain until reassigned.'
        : undefined,
    ].filter(Boolean);

    const confirmed = window.confirm(
      [
        `Are you absolutely sure you want to delete ${fullName} (${user.email})?`,
        'This will disable login, free the original email address for reuse, and keep historical records intact.',
        ...warnings,
      ].join('\n\n'),
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteUserForOwner({ userId: user._id }).unwrap();
      toast.success(`${fullName} was successfully deleted.`);
      showUserDetailsModal(false);
    } catch (error: any) {
      if (error?.status === 409) {
        const cancelAndDelete = window.confirm(
          [
            `${fullName} has an active paid subscription.`,
            'It would keep billing after deletion, because the user can no longer sign in to cancel it.',
            '',
            'Cancel the subscription IMMEDIATELY (no automatic refund is issued) and delete the user?',
          ].join('\n'),
        );
        if (!cancelAndDelete) {
          return;
        }
        try {
          await deleteUserForOwner({
            userId: user._id,
            subscriptionAction: 'cancel_immediate',
          }).unwrap();
          toast.success(
            `${fullName} and their subscription were successfully deleted.`,
          );
          showUserDetailsModal(false);
        } catch (retryError: any) {
          Sentry.captureException(retryError, {
            tags: {
              request: 'ownerDeleteUser',
            },
          });
          toast.error(
            retryError?.data?.message ||
              'Some unexpected error happened while trying to delete the user. Please try again later!',
          );
        }
        return;
      }
      Sentry.captureException(error, {
        tags: {
          request: 'ownerDeleteUser',
        },
      });
      toast.error(
        error?.data?.message ||
          'Some unexpected error happened while trying to delete the user. Please try again later!',
      );
    }
  };

  const emailStatusIcon = () => {
    if (user.emailStatus === 'bounced') {
      return (
        <span
          title={`Bounced: ${
            user.emailStatusReason || 'Email delivery failed'
          }`}
        >
          <ExclamationCircleIcon className="h-4 w-4 mr-1 text-orange-500" />
        </span>
      );
    }
    if (user.emailStatus === 'complained') {
      return (
        <span title="User marked emails as spam - do not send">
          <BanIcon className="h-4 w-4 mr-1 text-red-500" />
        </span>
      );
    }
    if (user.emailStatus === 'invalid') {
      return (
        <span title="Invalid email address">
          <ExclamationCircleIcon className="h-4 w-4 mr-1 text-red-500" />
        </span>
      );
    }
    return (
      <span title="Email is valid">
        <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
      </span>
    );
  };

  return (
    <div className="z-20 w-full">
      <div
        className="absolute top-2 right-2 cursor-pointer hover:bg-gray-100 rounded-full p-1 transition-colors"
        onClick={() => showUserDetailsModal(false)}
      >
        <XIcon className="h-5 w-5" />
      </div>
      <div className="text-sm text-slate-500 break-words mb-4">
        By signing in as {fullName}, you will be logged out from your account,
        and logged in to {user.firstName}'s account. All your actions will count
        as the actions of this user. Remember to log out when you finish. Do you
        want to proceed?
      </div>
      <div className="grid grid-flow-row bg-gray-100 rounded-xl p-4">
        <div className="grid grid-flow-row">
          <div className="grid grid-flow-row items-center divide-y">
            <div className="text-sm text-slate-400 pb-3 select-all break-all">
              {user._id}
            </div>
            {/* Name row */}
            <div className="grid grid-flow-col py-1 min-h-[61px] items-center transition-all duration-200">
              {isEditingName ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center w-full">
                  <div className="grid grid-cols-2 gap-1 -mb-4">
                    <Input
                      name="firstName"
                      placeholder="First name"
                      value={editedFirstName}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setEditedFirstName(event.target.value)
                      }
                    />
                    <Input
                      name="lastName"
                      placeholder="Last name"
                      value={editedLastName}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setEditedLastName(event.target.value)
                      }
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      action={handleNameChange}
                      disabled={isUpdatingUser}
                    >
                      Save
                    </Button>
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      buttonText="Cancel"
                      action={() => {
                        setIsEditingName(false);
                        setEditedFirstName(user.firstName);
                        setEditedLastName(user.lastName);
                      }}
                      disabled={isUpdatingUser}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>Name:</div>
                  <div className="grid grid-flow-col justify-self-end items-center gap-2">
                    <div className="text-right truncate font-medium">
                      {fullName}
                    </div>
                    {isOwner && (
                      <div
                        className="h-5 w-5 text-tttDefault hover:text-orange cursor-pointer flex-shrink-0"
                        title="Edit name"
                        onClick={() => setIsEditingName(true)}
                      >
                        <PencilAltIcon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Email row */}
            <div className="grid grid-flow-col py-1 min-h-[61px] items-center transition-all duration-200">
              {isEditingEmail ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center w-full">
                  <div className="-mb-4">
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={editedEmail}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setEditedEmail(event.target.value)
                      }
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      action={handleEmailChange}
                      disabled={isUpdatingUser}
                    >
                      Save
                    </Button>
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      buttonText="Cancel"
                      action={() => {
                        setIsEditingEmail(false);
                        setEditedEmail(user.email);
                      }}
                      disabled={isUpdatingUser}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1">
                    <span>Email:</span>
                    {emailStatusIcon()}
                  </div>
                  <div className="grid grid-flow-col justify-self-end items-center gap-2">
                    <div
                      className={`text-right truncate ${
                        user.emailStatus === 'bounced'
                          ? 'text-orange-500'
                          : user.emailStatus === 'complained' ||
                            user.emailStatus === 'invalid'
                          ? 'text-red-500'
                          : ''
                      }`}
                    >
                      {user.email}
                    </div>
                    {isOwner && (
                      <div
                        className="h-5 w-5 text-tttDefault hover:text-orange cursor-pointer flex-shrink-0"
                        title="Edit email"
                        onClick={() => setIsEditingEmail(true)}
                      >
                        <PencilAltIcon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {user.emailStatusReason &&
              (user.emailStatus === 'bounced' ||
                user.emailStatus === 'complained') && (
                <div className="grid grid-flow-col py-1">
                  <div className="text-sm text-gray-500">Email issue: </div>
                  <div className="grid justify-self-end text-right text-sm text-red-500">
                    {user.emailStatusReason}
                  </div>
                </div>
              )}
            {user.school && (
              <div className="grid grid-flow-col py-1">
                <div>School: </div>
                <div className="grid justify-self-end text-right">
                  {user.school}
                </div>
              </div>
            )}
            {user.roles.includes(ROLES.student) && (
              <>
                <div className="grid grid-flow-col py-1">
                  <div>Class: </div>
                  <div className="grid justify-self-end text-right">
                    {user.class}
                  </div>
                </div>
                <div className="grid grid-flow-col py-1">
                  <div>Student ID: </div>
                  <div className="grid justify-self-end text-right">
                    {user.studentId}
                  </div>
                </div>
              </>
            )}{' '}
            {user.roles.includes(ROLES.thinker) && (
              <>
                <div className="grid grid-flow-col py-1 min-h-[61px] items-center transition-all duration-200">
                  {isChangingTrack ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center w-full">
                      <div>
                        <Select
                          className="text-sm"
                          value={findTrackOption(
                            trackSelectOptions,
                            selectedTrackId ?? '',
                          )}
                          formatOptionLabel={formatTrackOptionLabel}
                          onChange={(option: any) =>
                            setSelectedTrackId(
                              (option as TrackSelectOption)?.value,
                            )
                          }
                          options={trackSelectOptions}
                          isLoading={isLoadingTracks}
                          placeholder="Select track"
                          styles={compactSelectStyles}
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          bgColor={'bg-blue-300'}
                          width="w-full"
                          textSize="text-sm"
                          action={handleTrackChange}
                          disabled={isUpdatingUser}
                        >
                          Save
                        </Button>
                        <Button
                          bgColor={'bg-blue-300'}
                          width="w-full"
                          textSize="text-sm"
                          buttonText="Cancel"
                          action={() => {
                            setIsChangingTrack(false);
                            setSelectedTrackId(user.followsTracks?.[0]?._id);
                          }}
                          disabled={isUpdatingUser}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>Track: </div>
                      <div className="grid grid-flow-col justify-self-end items-center gap-2">
                        <div className="text-right truncate">
                          {user.followsTracks?.[0]?.track}
                        </div>
                        {isOwnerOrSysadmin && (
                          <div
                            className="h-5 w-5 text-tttDefault hover:text-orange cursor-pointer flex-shrink-0"
                            title="Change track"
                            onClick={() => setIsChangingTrack(true)}
                          >
                            <PencilAltIcon className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="grid grid-flow-col py-1">
                  <div>Schedule frequency: </div>
                  <div className="grid justify-self-end text-right">
                    {user.scheduleFrequency} per week
                  </div>
                </div>
                <div className="grid grid-flow-col py-1 min-h-[61px] items-center transition-all duration-200">
                  {isChangingMentor ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center w-full">
                      <div>
                        <Select
                          className="text-sm"
                          value={findMentorOption(
                            mentorSelectOptions,
                            selectedMentorId,
                          )}
                          onChange={(option: any) =>
                            setSelectedMentorId(option?.value)
                          }
                          options={mentorSelectOptions}
                          formatOptionLabel={(option) =>
                            formatMentorOptionLabel(option, { multiline: true })
                          }
                          isLoading={isLoadingMentors}
                          placeholder="Select mentor"
                          styles={mentorCompactSelectStyles}
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          bgColor={'bg-blue-300'}
                          width="w-full"
                          textSize="text-sm"
                          action={handleMentorReassignment}
                          disabled={isReassigningMentor}
                        >
                          Save
                        </Button>
                        <Button
                          bgColor={'bg-blue-300'}
                          width="w-full"
                          textSize="text-sm"
                          buttonText="Cancel"
                          action={() => {
                            setIsChangingMentor(false);
                            setSelectedMentorId(undefined);
                          }}
                          disabled={isReassigningMentor}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>Mentor: </div>
                      <div className="grid grid-flow-col justify-self-end items-center gap-2">
                        <div className="text-right truncate">{user.mentor}</div>
                        {isOwner && (
                          <div
                            className="h-5 w-5 text-tttDefault hover:text-orange cursor-pointer flex-shrink-0"
                            title="Change mentor"
                            onClick={() => setIsChangingMentor(true)}
                          >
                            <PencilAltIcon className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            <div className="grid grid-flow-col py-1">
              <div>Roles: </div>
              <div className="grid justify-self-end text-right">
                {user?.roles}
              </div>
            </div>
            {user?.lastQuiz && (
              <div className="grid grid-flow-col py-1">
                <div>{user.lastQuiz?.id} review:</div>
                <div className="grid justify-self-end">
                  {user?.lastQuiz?.didFinishReview
                    ? '✔'
                    : typeof user?.lastQuiz?.progress?.num === 'number'
                    ? `✘ (Problem ${user?.lastQuiz?.progress?.num + 1} done)`
                    : '✘'}
                </div>
              </div>
            )}
            <div className="grid grid-flow-col py-1 min-h-[61px] items-center transition-all duration-200">
              {isChangingPassword ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center w-full">
                  <div className="-mb-4">
                    <Input
                      name="password"
                      placeholder="New password"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setPassword(event.target.value);
                      }}
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      action={handlePasswordChange}
                      disabled={isLoading}
                    >
                      Change
                    </Button>
                    <Button
                      bgColor={'bg-blue-300'}
                      width="w-full"
                      textSize="text-sm"
                      buttonText="Cancel"
                      action={() => setIsChangingPassword(false)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="truncate pr-2">
                    Last password change:{' '}
                    {user?.changedPasswordAt
                      ? dayjs(user?.changedPasswordAt).format(
                          'MM/DD/YYYY HH:mm',
                        )
                      : 'never'}
                  </div>
                  <div
                    className="self-center justify-self-end h-5 w-5 text-tttDefault hover:text-orange cursor-pointer flex-shrink-0"
                    title="Change password of this user"
                    onClick={() => setIsChangingPassword(true)}
                  >
                    <PencilAltIcon className="h-5 w-5" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Extended Time Limit - only for students and thinkers */}
        {(user.roles.includes(ROLES.student) ||
          user.roles.includes(ROLES.thinker)) && (
          <div className="grid grid-flow-col py-1 border-t pt-2 mt-2">
            <div className="flex items-center gap-2">
              <span>Extended Time (2x)</span>
              <span className="text-xs text-gray-500">Quiz time doubled</span>
            </div>
            <div className="grid justify-self-end text-right">
              <SwitchButton
                id="extended-time-toggle"
                checked={user.extendedTimeLimit || false}
                loading={isTogglingExtendedTime}
                shouldHaveText={false}
                onChange={(enabled: boolean) =>
                  handleToggleExtendedTimeLimit(enabled)
                }
              />
            </div>
          </div>
        )}

        {/* Features */}
        <div className="grid grid-flow-row mt-4">
          <div className="font-medium mb-1 select-all">Features</div>
          {Object.values(FeatureFlag).map((feature) => (
            <div key={feature} className="grid grid-flow-col py-1">
              <div className="capitalize">{feature.split('-').join(' ')}</div>
              <div className="grid justify-self-end text-right">
                <SwitchButton
                  id={`${feature}-toggle`}
                  checked={user.featureFlags?.includes(feature) || false}
                  loading={isTogglingFeature}
                  shouldHaveText={false}
                  onChange={(enabled: boolean) =>
                    handleToggleFeature(feature, enabled)
                  }
                />
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="grid grid-flow-row justify-items-center items-center pt-6 gap-2">
          <Button
            action={async () => {
              if (user) {
                navigate(
                  `/system-logs/${user._id}/${user.firstName} ${user.lastName}`,
                );
              }
            }}
            bgColor={'bg-blue-300'}
            bgColorHover={'bg-blue-400'}
          >
            <span>
              <UsersIcon className="h-4 w-4 mr-2 mt-[-4px] inline" />
              Show logs
            </span>
          </Button>
          <Button
            action={async () => {
              if (user) {
                await switchUser(user?._id);
              }
            }}
            bgColor={'bg-blue-300'}
            bgColorHover={'bg-blue-400'}
            disabled={isSwitchingUser}
          >
            <span>
              <UsersIcon className="h-4 w-4 mr-2 mt-[-4px] inline" />
              Switch to user
            </span>
          </Button>
          {user.lastQuiz?.didFinishReview === false && (
            <Button
              action={handlePushThroughReview}
              bgColor={'bg-blue-300'}
              bgColorHover={'bg-blue-400'}
              disabled={isPushingThrough}
            >
              <span>
                <BadgeCheckIcon className="h-4 w-4 mr-2 mt-[-4px] inline" />
                Push through the solution review
              </span>
            </Button>
          )}
          {(user.roles.includes(ROLES.student) ||
            user.roles.includes(ROLES.thinker)) && (
            <Button
              action={() => handleRemoveResult(user)}
              bgColor={'bg-blue-300'}
              bgColorHover={'bg-blue-400'}
              disabled={!user.lastSubmittedAvailableQuiz || isDeletingResult}
              disabledProgress={
                user.lastSubmittedAvailableQuiz
                  ? undefined
                  : 'No current result to delete'
              }
            >
              <span>
                <TrashIcon className="h-4 w-4 mr-2 mt-[-4px] inline" />
                Remove {user.roles.includes(ROLES.student)
                  ? 'current'
                  : 'last'}{' '}
                ({user.lastSubmittedAvailableQuiz}) result
              </span>
            </Button>
          )}
          {isOwner && !user.roles.includes(ROLES.owner) && (
            <div className="w-full border-t border-red-200 mt-4 pt-4 grid justify-items-center">
              <div className="font-medium text-red-700 mb-2">Danger Zone</div>
              {isDeletingUser ? (
                <div className="grid gap-2 text-sm w-full">
                  <div className="text-red-700">
                    Deleting this user will prevent them from logging in and
                    free their email address for reuse. Historical records will
                    be kept.
                  </div>
                  {user.roles.includes(ROLES.mentor) && (
                    <div className="text-orange-600">
                      Warning: this mentor may have assigned thinkers. They will
                      remain assigned until reassigned.
                    </div>
                  )}
                  {user.roles.includes(ROLES.teacher) && (
                    <div className="text-orange-600">
                      Warning: this teacher may have assigned classes. Those
                      class references will remain until reassigned.
                    </div>
                  )}
                  <div>Type this email to confirm: {user.email}</div>
                  <Input
                    name="deleteConfirmation"
                    placeholder="User email"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setDeleteConfirmation(event.target.value)
                    }
                  />
                  <div
                    className={
                      isDeleteConfirmationMatching
                        ? 'text-green-700 font-medium'
                        : 'text-slate-500'
                    }
                  >
                    {isDeleteConfirmationMatching
                      ? 'Email matches. You can now confirm deletion.'
                      : 'Delete stays disabled until the email matches exactly.'}
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      bgColor="bg-rose-800"
                      bgColorHover="bg-red"
                      textColor="text-white"
                      disabledBgColor={
                        isDeleteConfirmationMatching
                          ? 'bg-rose-800'
                          : 'bg-red/10'
                      }
                      disabledTextColor={
                        isDeleteConfirmationMatching ? 'text-white' : 'text-red'
                      }
                      width="w-full"
                      textSize="text-sm"
                      buttonText="Delete user"
                      action={handleDeleteUser}
                      disabled={
                        !isDeleteConfirmationMatching || isDeletingUserForOwner
                      }
                      disabledProgress={
                        isDeleteConfirmationMatching
                          ? undefined
                          : 'Type email to confirm'
                      }
                    />
                    <Button
                      bgColor="bg-blue-300"
                      width="w-full"
                      textSize="text-sm"
                      buttonText="Cancel"
                      action={() => {
                        setIsDeletingUser(false);
                        setDeleteConfirmation('');
                      }}
                      disabled={isDeletingUserForOwner}
                    />
                  </div>
                </div>
              ) : (
                <div className="justify-self-center">
                  <Button
                    action={() => setIsDeletingUser(true)}
                    bgColor="bg-rose-800"
                    bgColorHover="bg-red"
                    textColor="text-white"
                  >
                    <span>
                      <TrashIcon className="h-4 w-4 mr-2 mt-[-4px] inline" />
                      Delete user
                    </span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
